/**
 * The Export menu (decision 77; `docs/design/DESIGN.md`, principle 5: one menu holds PDF, EPUB, Word and Bundle; the
 * library's Menu, its trigger the library's Button): each export of the open book as the toolbar's buttons start it
 * (parts 20, 21, 22, 24a), one of each kind at a time (K2), with a line on what it is for. A started export's Cancel takes
 * the focus (A24b); Word… opens its choice of look. The menu closes with the focus on its button before an item runs.
 */
import { mergeProps } from 'solid-js';
import { focusOn } from './announce.ts';
import Button from '../ui/Button.ts';
import Menu from '../ui/Menu.ts';
import type { MenuEntry, MenuTriggerProps } from '../ui/Menu.ts';
import { component } from '../ui/solid.ts';
import type { Child } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** Where the menu sits: its button's hook class, look and words. */
export interface ExportMenuOptions {
  /** The button's class: the hook the page and its tests find it by. */
  readonly class: string;
  readonly variant: 'primary' | 'secondary' | 'ghost';
  readonly size: 'sm' | 'md' | 'lg';
  readonly label: string;
  /** Whether the button shows the chevron after its words (the writer's, not on a phone). */
  readonly chevron?: () => boolean;
  /** Part 38c: a shared book's read view: its exports without the bundle (the record isn't the invitee's), never disabled by the open book. */
  readonly shared?: boolean;
}

/** The menu (M1–M8) of the open book's four exports. */
export function ExportMenu(shell: Shell, options: ExportMenuOptions): Child {
  const { t, ui } = shell;
  const busy = (kind: string, running: readonly string[]): boolean => running.includes(kind);
  const items: MenuEntry[] = [
    {
      class: 'export-pdf',
      label: t('export.pdf'),
      hint: t('export.pdfHint'),
      get disabled() {
        return busy(shell.exporter.state().kind, ['reading', 'exporting']);
      },
      onSelect: () => {
        shell.exporter.start();
        focusOn(shell, '.cancel-export');
      },
    },
    {
      class: 'export-epub',
      label: t('export.epub'),
      hint: t('export.epubHint'),
      get disabled() {
        return busy(shell.epub.state().kind, ['reading', 'exporting']);
      },
      onSelect: () => {
        shell.epub.start();
        focusOn(shell, '.cancel-epub');
      },
    },
    {
      class: 'export-docx',
      label: t('export.word'),
      hint: t('export.wordHint'),
      dialog: true,
      get disabled() {
        return busy(shell.docx.state().kind, ['reading', 'exporting']);
      },
      onSelect: () => ui.setDialog({ kind: 'docx' }),
    },
    ...(options.shared === true
      ? []
      : [
          { separator: true } as const,
          {
            class: 'export-bundle',
            label: t('export.bundle'),
            hint: t('export.bundleHint'),
            get disabled() {
              return busy(shell.bundler.state().kind, ['saving', 'exporting']);
            },
            onSelect: () => {
              shell.bundler.start();
              focusOn(shell, '.cancel-bundle');
            },
          },
        ]),
  ];
  return component(Menu, {
    label: options.label,
    class: 'export-menu',
    items,
    trigger: (props: MenuTriggerProps) =>
      component(
        Button,
        mergeProps(props, {
          variant: options.variant,
          size: options.size,
          class: options.class,
          get iconEnd() {
            return options.chevron?.() === true ? ('chevronDown' as const) : undefined;
          },
          get disabled() {
            return options.shared !== true && shell.workspace.book() === null;
          },
          children: options.label,
        }),
      ),
  });
}
